// Cart drawer — intercepts every "Add to Cart" form (product page + quick-add
// cards) so a shopper stays on the page instead of being redirected to /cart,
// and keeps the drawer + header badge in sync via the Ajax Cart API.
// Progressive enhancement: every form still has a real action="/cart/add" and
// the cart icon still links to /cart, so this degrades gracefully with JS off.

// Audit fix #7 — free delivery threshold in paise (₹999). Keep this in sync
// with sections/header.liquid's decus_free_shipping_threshold and the
// announcement bar copy if the threshold ever changes.
const FREE_SHIPPING_THRESHOLD_CENTS = 99900;

document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.getElementById('CartDrawer');
  if (!drawer) return;

  const backdrop = document.querySelector('[data-cart-drawer-backdrop]');
  const itemsWrap = drawer.querySelector('[data-cart-drawer-items]');
  const emptyWrap = drawer.querySelector('[data-cart-drawer-empty]');
  const footer = drawer.querySelector('[data-cart-drawer-footer]');
  const subtotalEl = drawer.querySelector('[data-cart-drawer-subtotal]');
  const countEl = drawer.querySelector('[data-cart-drawer-count]');
  const badgeEls = document.querySelectorAll('[data-cart-count]');
  const shippingWrap = drawer.querySelector('[data-cart-drawer-shipping]');
  const shippingMsg = drawer.querySelector('[data-cart-shipping-msg]');
  const shippingFill = drawer.querySelector('[data-cart-shipping-fill]');
  const errorBanner = drawer.querySelector('[data-cart-drawer-error]');
  let errorBannerTimer = null;

  function showLineError(message) {
    if (!errorBanner || !message) return;
    errorBanner.textContent = message;
    errorBanner.hidden = false;
    clearTimeout(errorBannerTimer);
    errorBannerTimer = setTimeout(() => { errorBanner.hidden = true; }, 4000);
  }

  function openDrawer() {
    drawer.classList.add('is-open');
    if (backdrop) backdrop.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    // Accessibility fix — inert must come off when the drawer opens, or its
    // own contents (close button, quantity inputs, checkout) become
    // unreachable by keyboard/screen reader/AI agent right when they're needed.
    drawer.inert = false;
    document.body.classList.add('cart-drawer-open');
  }
  function closeDrawer() {
    drawer.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    // Accessibility fix (Google audit — Accessibility + Agentic Browsing) —
    // aria-hidden alone hides the drawer from assistive tech announcements
    // but leaves its close button and "Continue Shopping" link tabbable.
    // inert actually removes the closed drawer from the tab order, clicks,
    // and the accessibility tree.
    drawer.inert = true;
    document.body.classList.remove('cart-drawer-open');
  }

  drawer.querySelectorAll('[data-cart-drawer-close]').forEach((btn) => btn.addEventListener('click', closeDrawer));
  if (backdrop) backdrop.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });

  document.querySelectorAll('[data-cart-drawer-toggle]').forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      openDrawer();
    });
  });

  function lineTemplate(item) {
    const variant = !item.product_has_only_default_variant && item.variant_title
      ? `<div class="cart-drawer-line-variant">${item.variant_title}</div>`
      : '';
    return `
      <div class="cart-drawer-line" data-line-key="${item.key}">
        <a href="${item.url}"><img src="${item.image}" alt="${item.title.replace(/"/g, '&quot;')}" loading="lazy"></a>
        <div class="cart-drawer-line-info">
          <a href="${item.url}" class="cart-drawer-line-title">${item.product_title}</a>
          ${variant}
          <div class="cart-drawer-line-price">${item.final_line_price_formatted}</div>
          <div class="cart-drawer-line-qty">
            <button type="button" class="qty-btn" data-qty-decrease aria-label="Decrease quantity">&minus;</button>
            <input type="number" value="${item.quantity}" min="0" data-qty-input aria-label="Quantity">
            <button type="button" class="qty-btn" data-qty-increase aria-label="Increase quantity">+</button>
          </div>
          <button type="button" class="cart-drawer-remove" data-cart-remove>Remove</button>
        </div>
      </div>`;
  }

  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    if (window.Shopify && Shopify.money_format) {
      return Shopify.money_format.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
    }
    return '₹' + amount;
  }

  function updateShippingProgress(cart) {
    if (!shippingWrap || !shippingMsg || !shippingFill) return;
    if (cart.item_count === 0) {
      shippingWrap.hidden = true;
      return;
    }
    shippingWrap.hidden = false;
    const remaining = FREE_SHIPPING_THRESHOLD_CENTS - cart.total_price;
    const pct = Math.min(100, (cart.total_price / FREE_SHIPPING_THRESHOLD_CENTS) * 100);
    shippingFill.style.width = pct + '%';
    shippingMsg.innerHTML = remaining > 0
      ? `Add <strong>${formatMoney(remaining)}</strong> more for free delivery`
      : `You've unlocked free delivery 🎉`;
  }

  function renderCart(cart) {
    updateShippingProgress(cart);

    if (countEl) countEl.textContent = `(${cart.item_count})`;
    badgeEls.forEach((el) => { el.textContent = cart.item_count; });

    if (cart.item_count === 0) {
      if (itemsWrap) { itemsWrap.hidden = true; itemsWrap.innerHTML = ''; }
      if (emptyWrap) emptyWrap.hidden = false;
      if (footer) footer.hidden = true;
      return;
    }

    if (emptyWrap) emptyWrap.hidden = true;
    if (footer) footer.hidden = false;
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    if (itemsWrap) {
      itemsWrap.hidden = false;
      itemsWrap.innerHTML = cart.items.map((item) => lineTemplate({
        key: item.key,
        url: item.url,
        title: item.title,
        product_title: item.product_title,
        variant_title: item.variant_title,
        product_has_only_default_variant: item.variant_title === 'Default Title' || !item.variant_title,
        image: item.image,
        quantity: item.quantity,
        final_line_price_formatted: formatMoney(item.final_line_price),
      })).join('');
    }
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  function refreshAndOpen() {
    return fetchCart().then((cart) => { renderCart(cart); openDrawer(); });
  }

  function changeLine(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    })
      .then((r) => r.json())
      .then((data) => {
        // A successful change returns a full cart object (has .items). A
        // rejected change (e.g. "Only 1 item was added to your cart due to
        // availability" on low-stock variants) returns a plain
        // {status, message, description} object instead — rendering that
        // directly as a cart produced "(undefined)" / "₹NaN" and a false
        // "unlocked free delivery" message. Surface the real reason instead,
        // and re-sync the drawer with the server's actual cart state.
        if (!data || !data.items) {
          showLineError((data && (data.description || data.message)) || 'That quantity is unavailable.');
          return fetchCart().then(renderCart);
        }
        renderCart(data);
      })
      .catch(() => {});
  }

  drawer.addEventListener('click', (e) => {
    const line = e.target.closest('[data-cart-drawer-line]') || e.target.closest('[data-line-key]');
    if (!line) return;
    const key = line.dataset.lineKey;
    const input = line.querySelector('[data-qty-input]');
    if (e.target.matches('[data-qty-increase]')) {
      changeLine(key, Number(input.value) + 1);
    } else if (e.target.matches('[data-qty-decrease]')) {
      changeLine(key, Math.max(0, Number(input.value) - 1));
    } else if (e.target.matches('[data-cart-remove]')) {
      changeLine(key, 0);
    }
  });

  drawer.addEventListener('change', (e) => {
    if (!e.target.matches('[data-qty-input]')) return;
    const line = e.target.closest('[data-line-key]');
    if (!line) return;
    changeLine(line.dataset.lineKey, Math.max(0, Number(e.target.value)));
  });

  document.querySelectorAll('form').forEach((form) => {
    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add(\.js)?(\?|$)/.test(action)) return;

    // Product-page forms carry a dedicated error banner
    // ([data-product-add-error]); quick-add cards on collection/home pages
    // generally don't, so we fall back to the button-text message for those.
    const addErrorEl = form.querySelector('[data-product-add-error]');
    let addErrorTimer = null;
    function showAddError(message) {
      if (!addErrorEl || !message) return;
      addErrorEl.textContent = message;
      addErrorEl.hidden = false;
      clearTimeout(addErrorTimer);
      addErrorTimer = setTimeout(() => { addErrorEl.hidden = true; }, 5000);
    }

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (addErrorEl) addErrorEl.hidden = true;
      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Adding…'; }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) {
            const message = data.description || data.message || 'That quantity is unavailable.';
            showAddError(message);
            if (submitBtn) submitBtn.textContent = addErrorEl ? originalText : message;
            setTimeout(() => { if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalText; } }, addErrorEl ? 200 : 1800);
            return;
          }
          if (submitBtn) {
            submitBtn.textContent = 'Added ✓';
            submitBtn.classList.add('is-added');
          }
          refreshAndOpen().finally(() => {
            setTimeout(() => {
              if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.textContent = originalText;
                submitBtn.classList.remove('is-added');
              }
            }, 1200);
          });
        })
        .catch(() => {
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalText; }
        });
    });
  });
});
